/**
 * PageSpeedDetailModal Component (Story 4.11, Task 5.2)
 *
 * Displays detailed PageSpeed/Lighthouse metrics for Mobile and Desktop.
 * Follows the PropertySelector dialog pattern — separate Dialog controlled by parent state.
 *
 * Features:
 * - Side-by-side Mobile/Desktop columns (stacked on mobile)
 * - Performance score gauge per strategy (color-coded)
 * - Core Web Vitals breakdown (FCP, LCP, TBT, CLS, SI) with rating badges
 * - CrUX section (only rendered when data exists)
 * - Contextual insight annotation below score
 * - "Copy Summary" button (formats scores as plain text)
 */

import { useMemo, useCallback, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Copy, Check, Globe, ExternalLink } from "lucide-react";
import { Button } from "@provenleap/ui";

// ============================================================================
// Types
// ============================================================================

export interface PageSpeedMetricRecord {
  _reportType: "lighthouse" | "crux";
  strategy: "mobile" | "desktop";
  performanceScore?: number | null;
  performanceRating?: "good" | "needs-improvement" | "poor";
  fcp?: number | null;
  fcpRating?: "good" | "needs-improvement" | "poor";
  lcp?: number | null;
  lcpRating?: "good" | "needs-improvement" | "poor";
  tbt?: number | null;
  tbtRating?: "good" | "needs-improvement" | "poor";
  cls?: number | null;
  clsRating?: "good" | "needs-improvement" | "poor";
  si?: number | null;
  siRating?: "good" | "needs-improvement" | "poor";
  // CrUX fields
  fcp_p75?: number | null;
  lcp_p75?: number | null;
  cls_p75?: number | null;
  inp_p75?: number | null;
  overall_category?: string | null;
}

interface PageSpeedDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  hostname: string | null;
  metrics: PageSpeedMetricRecord[];
  lastSyncAt: string | null;
}

// ============================================================================
// Helpers
// ============================================================================

/** Color class for a performance score */
function scoreColor(score: number | null | undefined): string {
  if (score == null) return "text-muted-foreground";
  if (score >= 90) return "text-green-600";
  if (score >= 50) return "text-orange-500";
  return "text-red-600";
}

/** Background for score ring */
function scoreBg(score: number | null | undefined): string {
  if (score == null) return "border-muted";
  if (score >= 90) return "border-green-500";
  if (score >= 50) return "border-orange-400";
  return "border-red-500";
}

/** Rating badge styles */
function ratingBadge(rating: string | undefined): { bg: string; text: string; label: string } {
  switch (rating) {
    case "good":
      return { bg: "bg-green-50 border-green-200", text: "text-green-700", label: "Good" };
    case "needs-improvement":
      return { bg: "bg-orange-50 border-orange-200", text: "text-orange-700", label: "Needs Work" };
    case "poor":
      return { bg: "bg-red-50 border-red-200", text: "text-red-700", label: "Poor" };
    default:
      return { bg: "bg-muted/50 border-muted", text: "text-muted-foreground", label: "N/A" };
  }
}

/** Format duration in ms to human-readable */
function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/** Format CLS (unitless, low values) */
function formatCls(value: number | null | undefined): string {
  if (value == null) return "—";
  return value.toFixed(3);
}

/** Contextual insight based on score */
function getInsight(score: number | null | undefined, worstMetric: string | null): string {
  if (score == null) return "No data available";
  if (score >= 90) return "Excellent — faster than most sites";
  if (score >= 50) {
    return worstMetric
      ? `Room for improvement — focus on ${worstMetric}`
      : "Room for improvement";
  }
  return worstMetric
    ? `Needs attention — ${worstMetric} is critically slow`
    : "Needs attention — significant performance issues detected";
}

/** Find the worst-rated metric name */
function findWorstMetric(record: PageSpeedMetricRecord): string | null {
  const metrics: Array<{ name: string; rating?: string }> = [
    { name: "FCP", rating: record.fcpRating },
    { name: "LCP", rating: record.lcpRating },
    { name: "TBT", rating: record.tbtRating },
    { name: "CLS", rating: record.clsRating },
    { name: "Speed Index", rating: record.siRating },
  ];

  const poor = metrics.filter((m) => m.rating === "poor");
  if (poor.length > 0) return poor[0]!.name;

  const needsWork = metrics.filter((m) => m.rating === "needs-improvement");
  if (needsWork.length > 0) return needsWork[0]!.name;

  return null;
}

/** Validate URL protocol before rendering as link */
function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

// ============================================================================
// Sub-components
// ============================================================================

function ScoreGauge({ score, label }: { score: number | null | undefined; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`flex items-center justify-center w-20 h-20 rounded-full border-4 ${scoreBg(score)}`}
      >
        <span className={`text-2xl font-bold ${scoreColor(score)}`}>
          {score ?? "—"}
        </span>
      </div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
    </div>
  );
}

function MetricRow({
  label,
  value,
  rating,
  isCls,
}: {
  label: string;
  value: number | null | undefined;
  rating?: string;
  isCls?: boolean;
}) {
  const badge = ratingBadge(rating);
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium tabular-nums">
          {isCls ? formatCls(value) : formatDuration(value)}
        </span>
        <span
          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium border ${badge.bg} ${badge.text}`}
        >
          {badge.label}
        </span>
      </div>
    </div>
  );
}

function StrategyColumn({ record }: { record: PageSpeedMetricRecord }) {
  const worstMetric = findWorstMetric(record);
  const insight = getInsight(record.performanceScore, worstMetric);
  const strategyLabel = record.strategy === "mobile" ? "Mobile" : "Desktop";

  return (
    <div className="space-y-3">
      <div className="flex flex-col items-center gap-2">
        <ScoreGauge score={record.performanceScore} label={strategyLabel} />
        <p className="text-xs text-muted-foreground text-center max-w-[200px]">
          {insight}
        </p>
      </div>
      <div className="space-y-0.5">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
          Core Web Vitals
        </h4>
        <MetricRow label="FCP" value={record.fcp} rating={record.fcpRating} />
        <MetricRow label="LCP" value={record.lcp} rating={record.lcpRating} />
        <MetricRow label="TBT" value={record.tbt} rating={record.tbtRating} />
        <MetricRow label="CLS" value={record.cls} rating={record.clsRating} isCls />
        <MetricRow label="SI" value={record.si} rating={record.siRating} />
      </div>
    </div>
  );
}

function CruxSection({ record }: { record: PageSpeedMetricRecord }) {
  return (
    <div className="space-y-1.5">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Field Data (CrUX) — {record.strategy === "mobile" ? "Mobile" : "Desktop"}
      </h4>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {record.fcp_p75 != null && (
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">FCP p75</span>
            <span className="font-medium tabular-nums">{formatDuration(record.fcp_p75)}</span>
          </div>
        )}
        {record.lcp_p75 != null && (
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">LCP p75</span>
            <span className="font-medium tabular-nums">{formatDuration(record.lcp_p75)}</span>
          </div>
        )}
        {record.cls_p75 != null && (
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">CLS p75</span>
            <span className="font-medium tabular-nums">{formatCls(record.cls_p75)}</span>
          </div>
        )}
        {record.inp_p75 != null && (
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">INP p75</span>
            <span className="font-medium tabular-nums">{formatDuration(record.inp_p75)}</span>
          </div>
        )}
      </div>
      {record.overall_category && (
        <p className="text-xs text-muted-foreground">
          Overall: <span className="font-medium">{record.overall_category}</span>
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function PageSpeedDetailModal({
  open,
  onOpenChange,
  url,
  hostname,
  metrics,
  lastSyncAt,
}: PageSpeedDetailModalProps) {
  const [copied, setCopied] = useState(false);

  const { mobile, desktop, mobileCrux, desktopCrux } = useMemo(() => {
    const m = metrics.find(
      (r) => r._reportType === "lighthouse" && r.strategy === "mobile"
    ) ?? null;
    const d = metrics.find(
      (r) => r._reportType === "lighthouse" && r.strategy === "desktop"
    ) ?? null;
    const mc = metrics.find(
      (r) => r._reportType === "crux" && r.strategy === "mobile"
    ) ?? null;
    const dc = metrics.find(
      (r) => r._reportType === "crux" && r.strategy === "desktop"
    ) ?? null;
    return { mobile: m, desktop: d, mobileCrux: mc, desktopCrux: dc };
  }, [metrics]);

  const handleCopySummary = useCallback(() => {
    const lines: string[] = [];
    lines.push(`PageSpeed Report: ${hostname ?? url}`);
    lines.push(`URL: ${url}`);
    if (lastSyncAt) {
      lines.push(`Analyzed: ${new Date(lastSyncAt).toLocaleString()}`);
    }
    lines.push("");

    if (mobile) {
      lines.push(`Mobile: ${mobile.performanceScore ?? "—"}/100`);
      lines.push(`  FCP: ${formatDuration(mobile.fcp)} | LCP: ${formatDuration(mobile.lcp)} | TBT: ${formatDuration(mobile.tbt)} | CLS: ${formatCls(mobile.cls)} | SI: ${formatDuration(mobile.si)}`);
    }
    if (desktop) {
      lines.push(`Desktop: ${desktop.performanceScore ?? "—"}/100`);
      lines.push(`  FCP: ${formatDuration(desktop.fcp)} | LCP: ${formatDuration(desktop.lcp)} | TBT: ${formatDuration(desktop.tbt)} | CLS: ${formatCls(desktop.cls)} | SI: ${formatDuration(desktop.si)}`);
    }

    navigator.clipboard
      .writeText(lines.join("\n"))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard API unavailable — silent fail
      });
  }, [mobile, desktop, hostname, url, lastSyncAt]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/35" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-xl border-[0.5px] border-input bg-background p-6 shadow-lg max-h-[85vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                PageSpeed Details
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* URL display */}
          <div className="flex items-center gap-2 mb-4">
            <DialogPrimitive.Description className="text-sm text-muted-foreground truncate">
              {hostname ?? url}
            </DialogPrimitive.Description>
            {isSafeUrl(url) && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Open URL in new tab"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>

          {/* Side-by-side Lighthouse */}
          {(mobile || desktop) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {mobile && <StrategyColumn record={mobile} />}
              {desktop && <StrategyColumn record={desktop} />}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                No performance data available yet. Try refreshing the analysis.
              </p>
            </div>
          )}

          {/* CrUX sections — only rendered when data exists */}
          {(mobileCrux || desktopCrux) && (
            <div className="border-t pt-3 mb-4 space-y-3">
              {mobileCrux && <CruxSection record={mobileCrux} />}
              {desktopCrux && <CruxSection record={desktopCrux} />}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between border-t pt-3">
            <span className="text-xs text-muted-foreground">
              {lastSyncAt
                ? `Last analyzed: ${new Date(lastSyncAt).toLocaleString()}`
                : "Not yet analyzed"}
            </span>
            <Button variant="outline" size="sm" onClick={handleCopySummary}>
              {copied ? (
                <>
                  <Check className="mr-1.5 h-3.5 w-3.5 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  Copy Summary
                </>
              )}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
